import React, { useEffect, useState } from "react";
import { Container, Alert, ConnectedAlert, ProgressBar } from "../../../components";
import { Text, Heading } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import ApprovalChoices from "./ApprovalChoices";
import InformationBlocks from "./InformationBlocks";
import ApprovalReceipt from "./ApprovalReceipt";
import RejectReceipt from "./RejectReceipt";
import { REALM, Realm } from "../../../../../constants";
import { RootState } from "../../../../../models/rootState";
import {
    isOrderNotFound as isOrderNotFoundSelector,
    getOrderItemGuid,
    getErrorMessage,
    getRealm,
    getOrderRef,
    isAllowedToOrder,
} from "../../../app/state/selectors";
import { selectors as AUTH_SELECTORS } from "../../../authentication";
import {
    getBankIdErrorMessage,
    isBankIdVerified as isBankIdVerifiedSelector,
} from "../../../authentication/state/selectors";
import {
    approvalFromFormerLegal,
    rejectFromFormerLegal,
} from "../../../checkout/state/actions/transferOwnershipActions";
import {
    getTransferBreakageFee,
    getTransferNewLegalOwnerField,
    getTransferNewIndividualLegalOwnerFullName,
    getHasRejectedTransferOrder,
    getHasSubmittedTransferOrder,
    getTransferDate,
    isExistingLegalOwnerVerified as isExistingLegalOwnerVerifiedSelector,
    getAdminNumber,
} from "../../../checkout/state/selectors/accountSelectors";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import TEXT from "../../text-consts";
import "./LandingPage.less";

interface StateProps {
    adminNumber: string;
    bankIdCustomer?: string;
    breakageFee: number;
    existingLegalOwner?: string;
    hasRejectedTransferOrder: boolean;
    hasSubmittedTransferOrder: boolean;
    isAllowedToSubmit: boolean;
    isBankIdVerified: boolean;
    isExistingLegalOwnerVerified: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    logicalErrorMessage?: string;
    newLegalOwner: string;
    orderItemGuid: string;
    orderRef: string;
    organizationName: string;
    realm: Realm;
    shouldShowBankId: boolean;
    transferDate?: string;
    verifiedCompanyAdmin?: string;
}

type ConsentPageToB2BAndB2CProps = StateProps;

const ConsentPageToB2BAndB2C: React.FC<ConsentPageToB2BAndB2CProps> = ({
    // Redux (data)
    isAllowedToSubmit,
    isFetchingData,
    isOrderNotFound,
    organizationName,
    breakageFee,
    hasRejectedTransferOrder,
    hasSubmittedTransferOrder,
    logicalErrorMessage,
    realm,
    orderRef,
}) => {
    const dispatch = useDispatch();

    const isB2BTransfer = !!organizationName || realm === REALM.B2B;
    const [hasClickedRejected, setHasClickedRejected] = useState(false);
    // Orders already submitted on page load are shown with an error message from the server instead of a receipt
    const [hasSubmittedOnLoad] = useState(hasSubmittedTransferOrder);

    useEffect(() => {
        if (hasRejectedTransferOrder) {
            setHasClickedRejected(true);
        }
    }, [hasRejectedTransferOrder]);

    if (isOrderNotFound) {
        return (
            <div className="consent-landing-page">
                <div className="consent-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.CONSENT_FORMER_LEGAL.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <section className="consent-landing-page__order-not-found">
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.CONSENT_FORMER_LEGAL.ORDER_NOT_FOUND.HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.CONSENT_FORMER_LEGAL.ORDER_NOT_FOUND.TEXT}</Text>
                        <div
                            className="paragraph"
                            dangerouslySetInnerHTML={{ __html: TEXT.CONSENT_FORMER_LEGAL.ORDER_NOT_FOUND.HELP_TEXT }}
                        />
                    </section>
                </Container>
            </div>
        );
    }

    if (hasClickedRejected) {
        return <RejectReceipt />;
    }

    if (hasSubmittedTransferOrder && !hasSubmittedOnLoad) {
        return <ApprovalReceipt />;
    }

    return (
        <div className="consent-landing-page">
            <div className="consent-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.CONSENT_FORMER_LEGAL.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} backgroundColor="white" />
            <ConnectedAlert />
            {logicalErrorMessage && (
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            )}
            {isAllowedToSubmit && (
                <Container size="medium">
                    <InformationBlocks.Ingress />

                    <ApprovalChoices
                        breakageFee={breakageFee}
                        isB2BTransfer={isB2BTransfer}
                        submitAction={() => dispatch(approvalFromFormerLegal(orderRef))}
                        rejectAction={(reasonText: string, reasonId: string) =>
                            dispatch(rejectFromFormerLegal(orderRef, reasonText, reasonId))
                        }
                    />
                </Container>
            )}
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    orderItemGuid: getOrderItemGuid(state),
    isExistingLegalOwnerVerified: isExistingLegalOwnerVerifiedSelector(state),
    orderRef: getOrderRef(state) || "",
    shouldShowBankId: AUTH_SELECTORS.shouldShowBankId(state),
    breakageFee: getTransferBreakageFee(state),
    adminNumber: getAdminNumber(state),
    organizationName: getTransferNewLegalOwnerField(state, "organization", "name"),
    newLegalOwner: getTransferNewIndividualLegalOwnerFullName(state),
    hasRejectedTransferOrder: getHasRejectedTransferOrder(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    transferDate: getTransferDate(state),
    logicalErrorMessage: getErrorMessage(state) || getBankIdErrorMessage(state),
    realm: getRealm(state),
    isBankIdVerified: isBankIdVerifiedSelector(state),
    isAllowedToSubmit: isAllowedToOrder(state),
});

export default connect(mapStateToProps)(ConsentPageToB2BAndB2C);
